import Cookies from 'js-cookie';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useCallback, useRef } from 'react';

import { BillingActions } from '@Actions/index';
import { NotificationType, showNotification } from '@Components/design-system/notification/notification';
import { fallsUnderInstant } from '@Modules/appointment/hooks/directional-engine/rules/initial';
import { IAnalytics } from '@Reducers/analytics';
import { IConsultationInvoice } from '@Reducers/billing';
import { IUserData } from '@Reducers/user';
import { useAppDispatch, useAppSelector } from '@Redux/hooks';
import { BillingSource, InvoiceMode } from '@Services/Billing/billing-payload';
import * as gtm from '@Utils/analytics/gtm';
import { FEATURE_FLAGS } from '@Utils/feature-flags';
import { decodeCookie } from '@Utils/helper';
import { LOG_ERROR } from '@Utils/logger';

import useAnasRouter from './anas-router';
import { Routes } from './anas-router/routes';
import usePayment from './payment';

const useBookInstantConsultation = () => {
  const dispatch = useAppDispatch();
  const router = useAnasRouter();
  const openPaymentModal = usePayment();
  const userData: IUserData = useAppSelector((state) => state.user.userData);
  const analytics: IAnalytics = useAppSelector((state) => state.analytics);
  const invoice: IConsultationInvoice = useAppSelector((state) => state.billing.consultationInvoice);
  const invoiceRef = useRef(null);
  invoiceRef.current = invoice;
  const isInternalMember = useFeatureFlagEnabled(FEATURE_FLAGS.INTERNAL_MEMBERS) ?? false;
  const isInternalMemberRef = useRef<boolean>(isInternalMember);
  isInternalMemberRef.current = isInternalMember;

  const bookInstantConsultation = useCallback(
    (payload: { typeId: string; excludedProviderIds: string[]; voucherCode: string; preferredLanguages: string[] }) => {
      if (!fallsUnderInstant()) {
        showNotification(
          NotificationType.WARNING,
          'Instant Consultation is currently unavailable. Please feel free to try booking again between 11:00 AM - 8:00 PM IST.',
          3000,
        );
        router.push({ route: Routes.DASHBOARD });
        return;
      }
      dispatch(
        BillingActions.CreateInstantConsultationInvoice({
          typeId: payload.typeId,
          mode: InvoiceMode.Prepaid,
          providerIds: [],
          billingSource: BillingSource.ALLO_INSTANT,
          startTime: new Date().toISOString(),
          excludedProviderIds: payload.excludedProviderIds,
          voucherCode: payload.voucherCode,
          preferredLanguages: payload.preferredLanguages,
          ignoreDuplicateConsultation: !!isInternalMemberRef.current,
        }),
      )
        .then(() => {
          openPaymentModal({ itemType: 'consultation' });
          gtm.userEvent({
            event: 'appointment_booked',
            phone: userData.phoneNumber,
            // eslint-disable-next-line unicorn/numeric-separators-style
            amount: 135700,
            utm_source: decodeCookie(Cookies.get('utm_source')) || analytics?.leadData?.utmSource,
            utm_campaign: decodeCookie(Cookies.get('utm_campaign')) || analytics?.leadData?.utmCampaign,
            acq_source: analytics?.leadData?.utmSource || decodeCookie(Cookies.get('utm_source')),
            fbclid: decodeCookie(Cookies.get('fbclid')),
            invoiceId: invoiceRef.current?.id,
            userId: userData.id,
          });
          return;
        })
        .catch((error) => {
          LOG_ERROR('Error: useBookInstantConsultation>>bookInstantConsultation', error);
        });
    },
    [userData, analytics],
  );

  return bookInstantConsultation;
};

export default useBookInstantConsultation;
